/**
 * ExpressiveEventsMosaic — §3 "media-forward consumer drama" events renderer.
 *
 * CSS-columns masonry: columns-2 lg:columns-3 gap-4.
 * Each card: break-inside-avoid rounded-2xl overflow-hidden relative group mb-4.
 * - Static thumbnail (full-width, object-cover) — using thumbnail_url directly;
 *   no hover-play parity attempt (EventMedia carries stateful showVideo and an
 *   intersection-reliant preload video; importing it here would couple this
 *   renderer to non-trivial state and break testability; static thumb + glass
 *   play badge is the specified design for the mosaic).
 * - Hover: scale-[1.02] + shadow-elevation-3 via group-hover classes.
 * - Bottom glass bar: camera name, relative time, detection chips.
 * - Centered glass play button for video cards.
 * - Floating selection checkbox top-left when selectionMode.
 *
 * Props: plain data + callbacks — no hooks, no queries.
 */

import { useState } from 'react'
import { Play, Image as ImageIcon } from 'lucide-react'
import { DetectionChip } from '@/components/ui/detection-chip'
import { cn, formatRelativeTime } from '@/lib/utils'
import type { Event } from '@/types'

// ── sub-components ────────────────────────────────────────────────────────────

function SelectBadge({ selected }: { selected: boolean }) {
  return (
    <div
      className={cn(
        'absolute top-2 left-2 z-10 h-5 w-5 rounded-full flex items-center justify-center',
        'bg-black/50 backdrop-blur-sm border-2 transition-colors',
        selected ? 'bg-primary border-primary' : 'border-white/70',
      )}
    >
      {selected && (
        <svg viewBox="0 0 8 8" className="h-2.5 w-2.5 fill-white">
          <path d="M1 4l2 2 4-4" stroke="white" strokeWidth="1.5" fill="none" />
        </svg>
      )}
    </div>
  )
}

function GlassPlayBadge() {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black/40 backdrop-blur-sm border border-white/30">
        <Play className="h-4 w-4 text-white fill-white ml-0.5" />
      </div>
    </div>
  )
}

// ── card ──────────────────────────────────────────────────────────────────────

function MosaicCard({
  event,
  index,
  selectionMode,
  selected,
  onEventClick,
  onToggleSelect,
}: {
  event: Event
  index: number
  selectionMode: boolean
  selected: boolean
  onEventClick: (i: number) => void
  onToggleSelect: (id: number) => void
}) {
  const [imgError, setImgError] = useState(false)
  const mediaType = event.media_details?.media_type ?? 'image'
  const isVideo = mediaType === 'video'

  const handleClick = () => {
    if (selectionMode) {
      onToggleSelect(event.id)
    } else {
      onEventClick(index)
    }
  }

  return (
    <div
      className={cn(
        'break-inside-avoid rounded-2xl overflow-hidden relative group mb-4 cursor-pointer',
        'transition-all duration-[260ms]',
        'hover:scale-[1.02] hover:shadow-[0_8px_30px_rgb(0,0,0,0.18)]',
        selected && 'ring-2 ring-primary ring-offset-1',
      )}
      onClick={handleClick}
    >
      {/* Floating checkbox — only in selection mode */}
      {selectionMode && <SelectBadge selected={selected} />}

      {/* Media — static thumbnail; play badge layered for videos */}
      <div className="relative w-full bg-muted" style={{ minHeight: 120 }}>
        {event.thumbnail_url && !imgError ? (
          <img
            src={event.thumbnail_url}
            alt={event.event_type}
            className="w-full object-cover block"
            style={{ display: 'block' }}
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="flex w-full items-center justify-center py-12">
            <ImageIcon className="h-8 w-8 text-muted-foreground/30" />
          </div>
        )}

        {/* Centered glass play badge for video cards */}
        {isVideo && <GlassPlayBadge />}
      </div>

      {/* Bottom glass info bar */}
      <div className="absolute bottom-0 inset-x-0 bg-black/50 backdrop-blur-sm px-3 py-2">
        <p className="text-[12px] font-medium text-white truncate leading-tight">
          {event.camera_name}
        </p>
        <p className="text-[10px] text-white/70 tabular-nums mt-0.5">
          {formatRelativeTime(event.occurred_at)}
        </p>
        {(event.detected_objects.length > 0 || event.audio_detections.length > 0) && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {event.detected_objects.slice(0, 2).map((cls) => (
              <DetectionChip key={cls} cls={cls} type="object" />
            ))}
            {event.audio_detections.slice(0, 1).map((d) => (
              <DetectionChip key={d.class_name} cls={d.class_name} confidence={d.confidence} type="audio" />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── main component ────────────────────────────────────────────────────────────

export interface ExpressiveEventsMosaicHandlers {
  onEventClick: (index: number) => void
  onToggleSelect: (id: number) => void
}

export interface ExpressiveEventsMosaicProps extends ExpressiveEventsMosaicHandlers {
  events: Event[]
  selectionMode: boolean
  selectedIds: Set<number>
}

export function ExpressiveEventsMosaic({
  events,
  selectionMode,
  selectedIds,
  onEventClick,
  onToggleSelect,
}: ExpressiveEventsMosaicProps) {
  return (
    <div
      className="columns-2 lg:columns-3 gap-4"
      data-testid="event-mosaic"
    >
      {events.map((event, index) => (
        <MosaicCard
          key={event.id}
          event={event}
          index={index}
          selectionMode={selectionMode}
          selected={selectedIds.has(event.id)}
          onEventClick={onEventClick}
          onToggleSelect={onToggleSelect}
        />
      ))}
    </div>
  )
}
